import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Bell, Mail, Trash2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/busquedas")({
  head: () => ({ meta: [{ title: "Búsquedas guardadas — Admin" }] }),
  component: SavedSearches,
});

type Row = {
  id: string;
  email: string;
  filters: Record<string, unknown> | null;
  created_at: string;
};

function SavedSearches() {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin", "saved-searches"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("saved_searches")
        .select("id, email, filters, created_at")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Row[];
    },
  });

  const remove = async (id: string) => {
    if (!confirm("¿Eliminar esta alerta?")) return;
    const { error } = await supabase.from("saved_searches").delete().eq("id", id);
    if (error) toast.error(error.message);
    else {
      toast.success("Eliminada");
      refetch();
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Búsquedas guardadas</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Visitantes que activaron alertas por email desde el buscador público.
          </p>
        </div>
        <div className="hidden md:flex items-center gap-2 rounded-full bg-accent/10 px-3 py-1 text-xs text-accent">
          <Bell className="h-3.5 w-3.5" /> {data?.length ?? 0} activas
        </div>
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-border bg-card">
        {isLoading ? (
          <div className="h-40 animate-pulse bg-secondary/60" />
        ) : !data || data.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">
            Sin alertas todavía. Cuando alguien guarde una búsqueda aparecerá acá.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Filtros</th>
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-3">
                    <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1.5 text-primary hover:underline">
                      <Mail className="h-3.5 w-3.5" /> {r.email}
                    </a>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(r.filters ?? {}).map(([k, v]) => (
                        <span key={k} className="rounded-md bg-secondary px-2 py-0.5 text-xs">
                          {k}: <span className="font-medium">{String(v)}</span>
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {new Date(r.created_at).toLocaleDateString("es-PY")}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => remove(r.id)}
                      className="inline-flex items-center gap-1 text-xs text-destructive hover:underline"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
